/**
 * 阶段 4：半透明毛玻璃分栏终局结算中心 (Split-Screen Glassmorphic Settlement & Honor Badge Controller)
 * 职责：
 * 1. 评估红蓝双方全员封盘、60s 追分倒计时、8:00 竞速限时或 2048 绝杀胜负条件
 * 2. 决胜瞬间先执行 1.15s 棋盘高光定格与礼花绽放，随后展开半透明毛玻璃「左终局盘面 + 右全员五列战报榜」
 * 3. 多维综合评分算法：评出胜方「🏆 MVP (全场最佳)」、败方「🔥 SVP (虽败犹荣)」及五维专项荣誉金章
 *    （👑破界宗师 / ⚔️重炮死神 / 🛡️幻影铁壁 / 🤝破局枢纽 / ⚡连击鬼才）
 * 4. 支持点击右侧任意选手行实时切换左侧 4x4 终局棋盘快照，并支持一键「👁️ 透视全场」查看 6 盘与聊天室
 */

import { escapeHtml, type ArenaDomRefs } from "./domRefs";
import type { ArenaRuntimeState, ParticipantRuntime } from "../state/arenaState";
import type { SoundEngine } from "../game/soundEngine";
import type { DmcRankManager } from "../game/dmcRankManager";
import type { BattleEffects } from "./battleEffects";
import type { BattlefieldView } from "../game/battlefieldView";
import type { DmcGrade, TileData } from "../../shared/protocol";

export const SPEED_MATCH_DURATION_SEC = 180; // 极速模式单局时长限制：180 秒 (3:00)
export const CLASSIC_MATCH_DURATION_SEC = 480; // 标准模式单局时长限制：480 秒 (8:00)

export interface HonorBadgeItem {
  id: string;
  label: string;
  icon: string;
  variant: "mvp" | "svp" | "titan" | "slayer" | "aegis" | "nexus" | "combo";
  desc: string;
}

export interface SettlementPlayerReport {
  id: string;
  name: string;
  team: "red" | "blue";
  isBot: boolean;
  isMe: boolean;
  score: number;
  maxTile: number;
  slashHits: number;
  scoreDrained: number;
  shieldBlocks: number;
  ejectCount: number;
  finalGrade: DmcGrade;
  rating: number;
  isMvp: boolean;
  isSvp: boolean;
  badges: HonorBadgeItem[];
  tiles: TileData[];
}

const GRADE_RANK_VALUE: Record<DmcGrade, number> = {
  D: 1,
  C: 2,
  B: 3,
  A: 4,
  S: 5,
  SS: 6,
  SSS: 7,
};

export class GameoverController {
  private gameoverPopupTimer: number | null = null;
  private chaseCountdownTimer: number | null = null;
  private chaseRemainingSec = 0;
  private chasingTeam: "red" | "blue" | null = null;
  private matchCountdownTimer: number | null = null;
  private matchRemainingSec = CLASSIC_MATCH_DURATION_SEC;
  private latestReports: SettlementPlayerReport[] = [];
  private selectedPlayerId: string | null = null;

  constructor(
    private readonly dom: ArenaDomRefs,
    private readonly state: ArenaRuntimeState,
    private readonly sounds: SoundEngine,
    private readonly dmc: DmcRankManager,
    private readonly effects: BattleEffects,
    private readonly view: BattlefieldView,
    private readonly callbacks: {
      onReturnToSeats: () => void;
      onReturnToModePortal: () => void;
      hasActiveOrTransferableEject?: (team: "red" | "blue") => boolean;
    }
  ) {
    this.bindEvents();
  }

  private bindEvents() {
    const handleReturnToSeats = () => {
      this.dom.elModalGameover.classList.remove("inspecting-board");
      this.dom.elModalGameover.style.display = "none";
      this.callbacks.onReturnToSeats();
    };

    this.dom.elBtnRematch.addEventListener("click", handleReturnToSeats);
    this.dom.elBtnInspectRematch.addEventListener("click", handleReturnToSeats);

    this.dom.elBtnBackLobby.addEventListener("click", () => {
      this.dom.elModalGameover.classList.remove("inspecting-board");
      this.dom.elModalGameover.style.display = "none";
      this.callbacks.onReturnToModePortal();
    });

    // 切换「透视全场」与「返回战报榜」
    this.dom.elBtnInspectBoard.addEventListener("click", () => {
      this.dom.elModalGameover.classList.add("inspecting-board");
    });

    this.dom.elBtnRestoreReport.addEventListener("click", () => {
      this.dom.elModalGameover.classList.remove("inspecting-board");
    });
  }

  public clearChaseCountdown() {
    if (this.chaseCountdownTimer !== null) {
      clearInterval(this.chaseCountdownTimer);
      this.chaseCountdownTimer = null;
    }
    this.chaseRemainingSec = 0;
    this.chasingTeam = null;
    this.dom.elPkChaseBanner.style.display = "none";
    this.dom.elPkChaseBanner.classList.remove("chase-urgent", "chase-critical");
  }

  public clearMatchTimer() {
    if (this.matchCountdownTimer !== null) {
      clearInterval(this.matchCountdownTimer);
      this.matchCountdownTimer = null;
    }
    this.dom.elHudMatchTimer.style.display = "none";
    this.dom.elHudMatchTimer.classList.remove(
      "timer-warn",
      "timer-urgent",
      "timer-critical",
      "timer-tick-pop"
    );
  }

  public clearTimers() {
    this.clearChaseCountdown();
    this.clearMatchTimer();
    if (this.gameoverPopupTimer !== null) {
      clearTimeout(this.gameoverPopupTimer);
      this.gameoverPopupTimer = null;
    }
  }

  private formatClock(totalSec: number): string {
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }

  private renderMatchTimerUI() {
    const sec = this.matchRemainingSec;
    const isUrgent = sec <= 30;
    const isCritical = sec <= 10;
    this.dom.elHudMatchTimer.textContent = isUrgent
      ? `🔥 ${sec}s`
      : `⏱ ${this.formatClock(sec)}`;
    this.dom.elHudMatchTimer.classList.toggle("timer-warn", sec <= 60 && sec > 30);
    this.dom.elHudMatchTimer.classList.toggle("timer-urgent", isUrgent && !isCritical);
    this.dom.elHudMatchTimer.classList.toggle("timer-critical", isCritical);

    if (isUrgent) {
      this.dom.elHudMatchTimer.classList.remove("timer-tick-pop");
      void this.dom.elHudMatchTimer.offsetWidth;
      this.dom.elHudMatchTimer.classList.add("timer-tick-pop");
    }
  }

  public getMatchRemainingSec(): number {
    return this.matchRemainingSec;
  }

  private isTimedMode(): boolean {
    return this.state.gameMode === "classic_2048" || this.state.gameMode === "speed_2048";
  }

  public setMatchRemainingSec(sec: number) {
    this.matchRemainingSec = Math.max(0, Math.round(sec));
    if (this.isTimedMode() && this.state.isMatchRunning) {
      this.dom.elHudMatchTimer.style.display = "inline-flex";
      this.renderMatchTimerUI();
    }
  }

  /**
   * 启动极速模式 (3:00 / 180s) 或标准模式 (8:00 / 480s) 局内倒计时
   */
  public startMatchTimer() {
    this.clearMatchTimer();
    if (!this.isTimedMode()) return;

    this.matchRemainingSec =
      this.state.gameMode === "speed_2048"
        ? SPEED_MATCH_DURATION_SEC
        : CLASSIC_MATCH_DURATION_SEC;
    this.dom.elHudMatchTimer.style.display = "inline-flex";
    this.renderMatchTimerUI();

    this.matchCountdownTimer = window.setInterval(() => {
      if (this.state.hasFinished || !this.state.isMatchRunning) {
        this.clearMatchTimer();
        return;
      }

      this.matchRemainingSec = Math.max(0, this.matchRemainingSec - 1);
      this.renderMatchTimerUI();

      if (this.matchRemainingSec <= 0) {
        this.clearMatchTimer();
        this.clearChaseCountdown();
        const redAvg = this.state.getTeamAvgScore("red");
        const blueAvg = this.state.getTeamAvgScore("blue");
        const redMax = this.state.getTeamMaxTile("red");
        const blueMax = this.state.getTeamMaxTile("blue");
        const viewTeam = this.state.getViewTeam();
        const redWins = redAvg !== blueAvg ? redAvg > blueAvg : redMax >= blueMax;
        const isMyWin = (viewTeam === "red") === redWins;
        const limitLabel = this.state.gameMode === "speed_2048" ? "3:00" : "8:00";
        this.triggerGameOver(
          isMyWin,
          `${limitLabel} 限时到 · 红方 (${redAvg}) vs 蓝方 (${blueAvg})`
        );
      }
    }, 1000);
  }

  private updateChaseBannerText(
    chasingTeam: "red" | "blue",
    stuckTeam: "red" | "blue",
    targetScore: number
  ) {
    const stuckLabel = stuckTeam === "red" ? "红方" : "蓝方";
    const chaseLabel = chasingTeam === "red" ? "红方" : "蓝方";
    const isUrgent = this.chaseRemainingSec <= 20;
    const isCritical = this.chaseRemainingSec <= 10;
    this.dom.elPkChaseText.innerHTML = `<span class="chase-sec-badge">🔥 ${this.chaseRemainingSec}s</span><span class="chase-desc-text">${stuckLabel}封盘 · ${chaseLabel}需超 ${targetScore}</span>`;
    this.dom.elPkChaseBanner.classList.toggle("chase-urgent", isUrgent);
    this.dom.elPkChaseBanner.classList.toggle("chase-critical", isCritical);
  }

  private startOrUpdateChaseCountdown(
    chasingTeam: "red" | "blue",
    stuckTeam: "red" | "blue",
    targetScore: number
  ) {
    if (this.chaseCountdownTimer !== null && this.chasingTeam === chasingTeam) {
      this.updateChaseBannerText(chasingTeam, stuckTeam, targetScore);
      return;
    }

    this.clearChaseCountdown();
    this.chasingTeam = chasingTeam;
    this.chaseRemainingSec = 60;
    this.dom.elPkChaseBanner.style.display = "inline-flex";
    this.updateChaseBannerText(chasingTeam, stuckTeam, targetScore);

    this.chaseCountdownTimer = window.setInterval(() => {
      if (this.state.hasFinished || !this.state.isMatchRunning) {
        this.clearChaseCountdown();
        return;
      }

      this.chaseRemainingSec = Math.max(0, this.chaseRemainingSec - 1);
      const latestTarget = this.state.getTeamAvgScore(stuckTeam);
      const latestChase = this.state.getTeamAvgScore(chasingTeam);

      if (latestChase > latestTarget) {
        this.evaluateTeamStuckConditions();
        return;
      }

      this.updateChaseBannerText(chasingTeam, stuckTeam, latestTarget);

      if (this.chaseRemainingSec <= 0) {
        this.clearChaseCountdown();
        const viewTeam = this.state.getViewTeam();
        const isMyWin = viewTeam === stuckTeam;
        const stuckLabel = stuckTeam === "red" ? "红方" : "蓝方";
        const chaseLabel = chasingTeam === "red" ? "红方" : "蓝方";
        this.triggerGameOver(
          isMyWin,
          `60s 追分结束 · ${chaseLabel} (${latestChase}) 未能超越${stuckLabel} (${latestTarget})`
        );
      }
    }, 1000);
  }

  /**
   * 评估团队封盘、提前反超与 60s 追分决胜倒计时条件
   */
  public evaluateTeamStuckConditions() {
    if (this.state.hasFinished || !this.state.isMatchRunning) return;

    const redMembers = Array.from(this.state.participants.values()).filter(
      (p) => p.team === "red"
    );
    const blueMembers = Array.from(this.state.participants.values()).filter(
      (p) => p.team === "blue"
    );
    if (redMembers.length === 0 || blueMembers.length === 0) return;

    const viewTeam = this.state.getViewTeam();

    // 若某一方所有成员均已提前退出/断线（且另一方仍有选手在场），则未退出方直接获胜
    const allRedExited = redMembers.every((p) => Boolean(p.isExited));
    const allBlueExited = blueMembers.every((p) => Boolean(p.isExited));
    if (allRedExited && !allBlueExited) {
      this.clearChaseCountdown();
      this.triggerGameOver(viewTeam === "blue", "红方全员退出 · 蓝方直接获胜");
      return;
    }
    if (allBlueExited && !allRedExited) {
      this.clearChaseCountdown();
      this.triggerGameOver(viewTeam === "red", "蓝方全员退出 · 红方直接获胜");
      return;
    }

    const allRedStuck =
      !this.callbacks.hasActiveOrTransferableEject?.("red") &&
      redMembers.every((p) =>
        p.id === this.state.myPeerId && this.state.myTeam !== "spectator"
          ? this.state.isBoardLocked
          : p.isStuck
      );
    const allBlueStuck =
      !this.callbacks.hasActiveOrTransferableEject?.("blue") &&
      blueMembers.every((p) =>
        p.id === this.state.myPeerId && this.state.myTeam !== "spectator"
          ? this.state.isBoardLocked
          : p.isStuck
      );

    if (!allRedStuck && !allBlueStuck) {
      if (this.chaseCountdownTimer !== null) {
        this.clearChaseCountdown();
      }
      return;
    }

    const redAvg = this.state.getTeamAvgScore("red");
    const blueAvg = this.state.getTeamAvgScore("blue");

    if (allRedStuck && allBlueStuck) {
      this.clearChaseCountdown();
      const redWins = redAvg >= blueAvg;
      const isMyWin = (viewTeam === "red") === redWins;
      this.triggerGameOver(
        isMyWin,
        `双方封盘 · 红方 (${redAvg}) vs 蓝方 (${blueAvg})`
      );
      return;
    }

    if (allRedStuck && !allBlueStuck) {
      if (blueAvg > redAvg) {
        this.clearChaseCountdown();
        const isMyWin = viewTeam === "blue";
        this.triggerGameOver(
          isMyWin,
          `红方封盘 · 蓝方 (${blueAvg}) 超越红方 (${redAvg})`
        );
      } else {
        this.startOrUpdateChaseCountdown("blue", "red", redAvg);
      }
      return;
    }

    if (allBlueStuck && !allRedStuck) {
      if (redAvg > blueAvg) {
        this.clearChaseCountdown();
        const isMyWin = viewTeam === "red";
        this.triggerGameOver(
          isMyWin,
          `蓝方封盘 · 红方 (${redAvg}) 超越蓝方 (${blueAvg})`
        );
      } else {
        this.startOrUpdateChaseCountdown("red", "blue", blueAvg);
      }
    }
  }

  /**
   * 汇总全员终局遥测数据，计算综合评分 (4.8 ~ 16.0)、胜方 MVP、败方 SVP 与五维专项荣誉徽章
   */
  private computeMatchReports(winningTeam: "red" | "blue"): SettlementPlayerReport[] {
    // 先把本机人类选手的最新盘面与战绩同步回 participants
    if (this.state.myTeam !== "spectator") {
      const me = this.state.participants.get(this.state.myPeerId);
      if (me) {
        me.score = this.state.localScore;
        me.maxTile = this.state.localMaxTile;
        me.slashHits = Math.max(me.slashHits, this.state.slashHits);
        me.scoreDrained = Math.max(me.scoreDrained, this.state.scoreDrained);
        me.shieldBlocks = Math.max(me.shieldBlocks, this.state.shieldBlocks);
        this.state.updateParticipantPeakGrade(me, this.dmc.grade);
        me.tiles = this.state.localTiles.map((t) => ({
          id: t.id,
          r: t.r,
          c: t.c,
          val: t.val,
          isObstacle: t.isObstacle,
          isTransferred: t.isTransferred,
          isLastNative: t.isLastNative,
          isPendingEject: t.isPendingEject,
        }));
      }
    }

    // 若房主托管了 AI，也确保获取 AI 最新盘面快照
    if (this.state.isRoomHost()) {
      for (const [botId, bot] of this.state.activeBots.entries()) {
        const botRuntime = this.state.participants.get(botId);
        if (botRuntime) {
          const liveTiles = bot.getTiles();
          if (liveTiles.length > 0) {
            botRuntime.tiles = liveTiles.map((t) => ({
              id: t.id,
              r: t.r,
              c: t.c,
              val: t.val,
              isObstacle: t.isObstacle,
              isTransferred: t.isTransferred,
              isLastNative: t.isLastNative,
              isPendingEject: t.isPendingEject,
            }));
          }
        }
      }
    }

    const allParticipants: ParticipantRuntime[] = Array.from(
      this.state.participants.values()
    );
    const matchMaxScore = Math.max(1, ...allParticipants.map((p) => p.score));
    const matchMaxTile = Math.max(2, ...allParticipants.map((p) => p.maxTile));
    const maxDrained = Math.max(0, ...allParticipants.map((p) => p.scoreDrained));
    const maxSlash = Math.max(0, ...allParticipants.map((p) => p.slashHits));
    const maxBlocks = Math.max(0, ...allParticipants.map((p) => p.shieldBlocks));
    const maxEjects = Math.max(0, ...allParticipants.map((p) => p.ejectCount));

    const reports: SettlementPlayerReport[] = allParticipants.map((p) => {
      const scoreNorm = p.score / matchMaxScore; // 0 ~ 1.0
      const tileLog = Math.log2(Math.max(2, p.maxTile)) / 11; // 2048 -> 1.0
      const tileBonus = p.maxTile >= 2048 ? 0.85 : 0;
      const combatImpact = Math.min(
        3.2,
        p.slashHits * 0.35 +
          (p.scoreDrained / 1200) * 0.65 +
          p.shieldBlocks * 0.35 +
          p.ejectCount * 0.45
      );
      const winBonus = p.team === winningTeam ? 0.8 : 0;
      const rawRating =
        5.0 + scoreNorm * 4.8 + tileLog * 2.8 + tileBonus + combatImpact + winBonus;
      const rating = Math.round(Math.min(16.0, Math.max(4.8, rawRating)) * 10) / 10;

      // 结合峰值华丽度与本局综合评分给出最终评级勋章
      let ratingGrade: DmcGrade = "C";
      if (rating >= 13.8 || p.maxTile >= 2048) ratingGrade = "SSS";
      else if (rating >= 12.2) ratingGrade = "SS";
      else if (rating >= 10.6) ratingGrade = "S";
      else if (rating >= 8.8) ratingGrade = "A";
      else if (rating >= 7.2) ratingGrade = "B";

      const finalGrade =
        (GRADE_RANK_VALUE[p.peakGrade] || 1) >= (GRADE_RANK_VALUE[ratingGrade] || 1)
          ? p.peakGrade
          : ratingGrade;

      return {
        id: p.id,
        name: p.name,
        team: p.team,
        isBot: p.isBot,
        isMe: p.id === this.state.myPeerId && this.state.myTeam !== "spectator",
        score: p.score,
        maxTile: p.maxTile,
        slashHits: p.slashHits,
        scoreDrained: p.scoreDrained,
        shieldBlocks: p.shieldBlocks,
        ejectCount: p.ejectCount,
        finalGrade,
        rating,
        isMvp: false,
        isSvp: false,
        badges: [],
        tiles: p.tiles.map((t) => ({ ...t })),
      };
    });

    // 排序辅助：按综合评分 -> 得分 -> 最大块 -> 削分
    const sortByPerformance = (a: SettlementPlayerReport, b: SettlementPlayerReport) => {
      if (b.rating !== a.rating) return b.rating - a.rating;
      if (b.score !== a.score) return b.score - a.score;
      if (b.maxTile !== a.maxTile) return b.maxTile - a.maxTile;
      return b.scoreDrained - a.scoreDrained;
    };

    const winTeamReports = reports
      .filter((r) => r.team === winningTeam)
      .sort(sortByPerformance);
    const loseTeamReports = reports
      .filter((r) => r.team !== winningTeam)
      .sort(sortByPerformance);

    // 1. 颁发胜方 MVP 与败方 SVP
    if (winTeamReports[0]) {
      winTeamReports[0].isMvp = true;
      winTeamReports[0].badges.push({
        id: "mvp",
        label: "MVP",
        icon: "🏆",
        variant: "mvp",
        desc: "胜方全场最佳统帅",
      });
    }
    if (loseTeamReports[0]) {
      loseTeamReports[0].isSvp = true;
      loseTeamReports[0].badges.push({
        id: "svp",
        label: "SVP",
        icon: "🔥",
        variant: "svp",
        desc: "败方虽败犹荣核心",
      });
    }

    // 2. 颁发五维专项荣誉金章（每人最多叠加 2 枚最具含金量的专项金章，连同 MVP/SVP 最多展示 3 枚）
    for (const r of reports) {
      const specialist: HonorBadgeItem[] = [];

      // (1) 👑 破界宗师：合出 2048 或全场最大块 (>= 256)
      if (r.maxTile >= 2048 || (r.maxTile >= 256 && r.maxTile === matchMaxTile)) {
        specialist.push({
          id: "titan",
          label: r.maxTile >= 2048 ? "2048破界" : "合成宗师",
          icon: "👑",
          variant: "titan",
          desc: `合出全场最高阶数字块 ${r.maxTile}`,
        });
      }

      // (2) ⚔️ 重炮死神：全场最高削分 (>= 64) 或最高斩击次数 (>= 2)
      if (
        (maxDrained >= 64 && r.scoreDrained === maxDrained) ||
        (maxSlash >= 2 && r.slashHits === maxSlash)
      ) {
        specialist.push({
          id: "slayer",
          label: "重炮死神",
          icon: "⚔️",
          variant: "slayer",
          desc: `重炮劈砍 ${r.slashHits} 次，累计削减敌方 ${r.scoreDrained} 分`,
        });
      }

      // (3) 🛡️ 幻影铁壁：全场最高闪避/护盾格挡 (>= 1)
      if (maxBlocks >= 1 && r.shieldBlocks === maxBlocks) {
        specialist.push({
          id: "aegis",
          label: "幻影铁壁",
          icon: "🛡️",
          variant: "aegis",
          desc: `凭借身法闪避与护盾抵御敌方重炮 ${r.shieldBlocks} 次`,
        });
      }

      // (4) 🤝 破局枢纽：封盘互传次数最多 (>= 1)
      if (maxEjects >= 1 && r.ejectCount === maxEjects) {
        specialist.push({
          id: "nexus",
          label: "破局枢纽",
          icon: "🤝",
          variant: "nexus",
          desc: `封盘互传 ${r.ejectCount} 次，盘活团队棋盘空间`,
        });
      }

      // (5) ⚡ 连击鬼才：达到 SSS 或 SS 华丽度评级
      if (r.finalGrade === "SSS" || r.finalGrade === "SS") {
        specialist.push({
          id: "combo",
          label: "连击鬼才",
          icon: "⚡",
          variant: "combo",
          desc: `局内达成 ${r.finalGrade} 级高速连击评价`,
        });
      }

      r.badges.push(...specialist.slice(0, 2));
    }

    return [...winTeamReports, ...loseTeamReports];
  }

  /**
   * 将指定选手的终局 4x4 盘面快照渲染到左侧展台（统一 16 格网格，确保空格底色与数字块严丝合缝）
   */
  private renderFocusPlayerSnapshot(playerId: string) {
    const report =
      this.latestReports.find((r) => r.id === playerId) || this.latestReports[0];
    if (!report) return;

    this.selectedPlayerId = report.id;

    // 同步右侧表格行选中态
    this.dom.elSettlementScoreboardBody
      .querySelectorAll(".settlement-player-row")
      .forEach((row) => {
        row.classList.toggle(
          "is-selected",
          row.getAttribute("data-player-id") === report.id
        );
      });

    // 1. 左侧展台头部信息（仅展示选手名 + 最核心的 1 枚主勋章 + 评分）
    this.dom.elSettlementFocusTeamDot.className = `showcase-team-dot ${
      report.team === "red" ? "dot-red" : "dot-blue"
    }`;
    this.dom.elSettlementFocusName.textContent = report.isMe
      ? `${report.name} (我)`
      : report.name;

    const topBadge = report.badges[0];
    this.dom.elSettlementFocusBadges.innerHTML = topBadge
      ? `<span class="honor-badge badge-${topBadge.variant}" title="${escapeHtml(
          topBadge.desc
        )}">${topBadge.icon} ${escapeHtml(topBadge.label)}</span>`
      : "";

    this.dom.elSettlementFocusRating.textContent = report.rating.toFixed(1);
    this.dom.elStatMyRank.textContent = report.finalGrade;
    this.dom.elStatMyRank.className = `rank-prestige-badge rank-${report.finalGrade.toLowerCase()}`;

    // 2. 渲染完整 4x4 16 格盘面快照（每个坐标格渲染背景底色，若有方块则在其内渲染真实糖果色数字块）
    const tileMap = new Map<string, TileData>();
    for (const t of report.tiles) {
      tileMap.set(`${t.r}_${t.c}`, t);
    }

    const cellsHtml: string[] = [];
    for (let r = 0; r < 4; r++) {
      for (let c = 0; c < 4; c++) {
        const t = tileMap.get(`${r}_${c}`);
        if (!t) {
          cellsHtml.push(`<div class="snapshot-cell-slot"></div>`);
        } else {
          const tileCls = t.isObstacle
            ? "tile-obstacle"
            : t.val > 2048
              ? "tile-super"
              : `tile-${t.val}`;
          const pendingCls = t.isPendingEject ? "tile-pending-eject" : "";
          const label = t.isObstacle ? "🔒" : String(t.val);
          cellsHtml.push(`
            <div class="snapshot-cell-slot">
              <div class="snapshot-tile ${pendingCls}">
                <div class="tile-inner ${tileCls}">${label}</div>
              </div>
            </div>
          `);
        }
      }
    }
    this.dom.elSettlementSnapshotTiles.innerHTML = cellsHtml.join("");
  }

  /**
   * 渲染右侧无边框红蓝双阵营五列战报榜（单行紧凑排版，3v3 全员 6 人一屏完整展示无需滚动）
   */
  private renderScoreboardTable(winningTeam: "red" | "blue") {
    const teamOrder: Array<"red" | "blue"> =
      winningTeam === "red" ? ["red", "blue"] : ["blue", "red"];

    let rowGlobalIdx = 0;
    const htmlBlocks: string[] = [];

    for (const team of teamOrder) {
      const teamReports = this.latestReports.filter((r) => r.team === team);
      if (teamReports.length === 0) continue;

      const isWinner = team === winningTeam;
      const teamName = team === "red" ? "红方阵营" : "蓝方阵营";
      const teamMaxScore = this.state.getTeamMaxScore(team);
      const verdictPill = isWinner
        ? `<span class="team-verdict-pill pill-win">🏆 胜利</span>`
        : `<span class="team-verdict-pill pill-lose">败北</span>`;

      const rowsHtml = teamReports
        .map((r) => {
          const idx = rowGlobalIdx++;
          const isSelected = r.id === this.selectedPlayerId;
          const tileCls = r.maxTile > 2048 ? "tile-super" : `tile-${r.maxTile}`;
          // 同行最多展示 2 枚勋章（主勋章 MVP/SVP + 1 枚最具代表性的专项章），杜绝换行撑高
          const inlineBadges = r.badges.slice(0, 2);
          const badgesHtml = inlineBadges
            .map(
              (b) =>
                `<span class="honor-badge badge-${b.variant}" style="--badge-delay: ${
                  idx * 65 + 200
                }ms" title="${escapeHtml(b.desc)}">${b.icon} ${escapeHtml(b.label)}</span>`
            )
            .join("");

          const combatSegs: string[] = [];
          if (r.slashHits > 0) {
            combatSegs.push(`<em class="c-slash">斩${r.slashHits}</em>`);
          }
          if (r.scoreDrained > 0) {
            combatSegs.push(`<em class="c-drain">削${r.scoreDrained}</em>`);
          }
          if (r.shieldBlocks > 0) {
            combatSegs.push(`<em class="c-block">挡${r.shieldBlocks}</em>`);
          }
          if (r.ejectCount > 0) {
            combatSegs.push(`<em class="c-eject">传${r.ejectCount}</em>`);
          }
          const combatHtml =
            combatSegs.length > 0
              ? combatSegs.join('<span class="c-dot">·</span>')
              : `<span class="c-muted">发育蓄能</span>`;

          return `
            <div class="settlement-player-row ${isSelected ? "is-selected" : ""} ${
              r.isMe ? "is-me-row" : ""
            } ${r.isMvp ? "is-mvp-row" : ""} ${r.isSvp ? "is-svp-row" : ""}"
                 data-player-id="${escapeHtml(r.id)}"
                 style="--row-idx: ${idx};">
              <div class="col-player">
                <span class="row-team-dot ${team === "red" ? "dot-red" : "dot-blue"}"></span>
                <span class="row-player-name">${escapeHtml(r.name)}${
                  r.isMe ? '<em class="me-tag">我</em>' : ""
                }</span>
                <div class="player-badges-inline">${badgesHtml}</div>
              </div>

              <div class="col-tile">
                <span class="mini-tile-badge ${tileCls}">${r.maxTile}</span>
              </div>

              <div class="col-score">
                <span class="row-score-val">${r.score.toLocaleString()}</span>
              </div>

              <div class="col-combat">
                ${combatHtml}
              </div>

              <div class="col-rating">
                <span class="row-rating-num ${r.isMvp ? "mvp-score" : r.isSvp ? "svp-score" : ""}">${r.rating.toFixed(
                  1
                )}</span>
                <span class="row-grade-tag rank-${r.finalGrade.toLowerCase()}">${
                  r.finalGrade
                }</span>
              </div>
            </div>
          `;
        })
        .join("");

      htmlBlocks.push(`
        <div class="scoreboard-team-section team-sec-${team}">
          <div class="scoreboard-team-bar">
            <div class="team-bar-left">
              <span class="team-bar-name ${team}">${teamName}</span>
              ${verdictPill}
            </div>
            <span class="team-bar-score">最高分 <b>${teamMaxScore.toLocaleString()}</b></span>
          </div>
          <div class="scoreboard-rows-list">
            ${rowsHtml}
          </div>
        </div>
      `);
    }

    this.dom.elSettlementScoreboardBody.innerHTML = htmlBlocks.join("");

    // 绑定点击任意选手行切换左侧终局棋盘快照
    this.dom.elSettlementScoreboardBody
      .querySelectorAll(".settlement-player-row")
      .forEach((row) => {
        row.addEventListener("click", () => {
          const pid = row.getAttribute("data-player-id");
          if (pid) {
            this.sounds.playSlide();
            this.renderFocusPlayerSnapshot(pid);
          }
        });
      });
  }

  public triggerGameOver(isWin: boolean, reason: string) {
    if (this.state.hasFinished) return;
    this.state.hasFinished = true;
    this.state.isMatchRunning = false;
    if (this.state.roomId) {
      try {
        sessionStorage.removeItem(`pk2048_match_snapshot_${this.state.roomId}`);
      } catch (_) {}
    }
    for (const bot of this.state.activeBots.values()) {
      bot.stop();
    }
    this.clearTimers();
    this.dmc.stop();

    // 1. 决胜瞬间在棋盘原位点亮 1.15s 高光定格与顶层礼花
    this.dom.elLocalBoardWrapper.classList.remove("board-climax-win", "board-climax-lose");
    this.dom.elLocalBoardWrapper.classList.add(
      isWin ? "board-climax-win" : "board-climax-lose"
    );
    this.view.logCombat(isWin ? `VICTORY · ${reason}` : `DEFEAT · ${reason}`);

    if (isWin) {
      this.sounds.playVictory();
      this.effects.fireVictoryConfetti();
      this.dom.elGameoverBanner.textContent = "VICTORY";
      this.dom.elGameoverBanner.className = "gameover-banner win";
    } else {
      this.sounds.playDefeat();
      this.dom.elGameoverBanner.textContent = "DEFEAT";
      this.dom.elGameoverBanner.className = "gameover-banner lose";
    }

    // 2. 计算胜负阵营、红蓝比分、全员多维战报与 MVP/SVP 荣誉勋章
    const viewTeam = this.state.getViewTeam();
    const winningTeam: "red" | "blue" = isWin
      ? viewTeam
      : viewTeam === "red"
        ? "blue"
        : "red";

    const redMaxScore = this.state.getTeamMaxScore("red");
    const blueMaxScore = this.state.getTeamMaxScore("blue");

    this.dom.elGameoverCard.classList.toggle("is-win", isWin);
    this.dom.elGameoverCard.classList.toggle("is-lose", !isWin);
    this.dom.elGameoverModeTag.textContent =
      this.state.gameMode === "endless"
        ? "无限模式"
        : this.state.gameMode === "speed_2048"
          ? "极速模式"
          : "标准模式";
    this.dom.elGameoverReason.textContent = reason;

    this.dom.elStatMyScore.textContent = redMaxScore.toLocaleString();
    this.dom.elStatRivalScore.textContent = blueMaxScore.toLocaleString();

    const diff = redMaxScore - blueMaxScore;
    if (diff > 0) {
      this.dom.elGameoverScoreDiff.textContent = `红方 +${diff}`;
      this.dom.elGameoverScoreDiff.className = "vs-diff-pill diff-lead";
    } else if (diff < 0) {
      this.dom.elGameoverScoreDiff.textContent = `蓝方 +${Math.abs(diff)}`;
      this.dom.elGameoverScoreDiff.className = "vs-diff-pill diff-trail";
    } else {
      this.dom.elGameoverScoreDiff.textContent = "平分决胜";
      this.dom.elGameoverScoreDiff.className = "vs-diff-pill";
    }

    const totalScore = Math.max(1, redMaxScore + blueMaxScore);
    const redRatio =
      redMaxScore === 0 && blueMaxScore === 0
        ? 50
        : Math.min(92, Math.max(8, Math.round((redMaxScore / totalScore) * 100)));
    this.dom.elGameoverVsFillMy.style.width = `${redRatio}%`;

    this.latestReports = this.computeMatchReports(winningTeam);

    // 默认选中「本机玩家」（若为观战则默认选中胜方 MVP）
    const defaultFocus =
      this.latestReports.find((r) => r.isMe) ||
      this.latestReports.find((r) => r.isMvp) ||
      this.latestReports[0];
    this.selectedPlayerId = defaultFocus ? defaultFocus.id : null;

    this.renderScoreboardTable(winningTeam);
    if (this.selectedPlayerId) {
      this.renderFocusPlayerSnapshot(this.selectedPlayerId);
    }

    // 底部 MVP & SVP 荣耀播报栏
    const mvp = this.latestReports.find((r) => r.isMvp);
    const svp = this.latestReports.find((r) => r.isSvp);
    const calloutParts: string[] = [];
    if (mvp) {
      calloutParts.push(
        `🏆 胜方 MVP：<b>${escapeHtml(mvp.name)}</b> (${mvp.rating.toFixed(1)}分)`
      );
    }
    if (svp) {
      calloutParts.push(
        `🔥 败方 SVP：<b>${escapeHtml(svp.name)}</b> (${svp.rating.toFixed(1)}分)`
      );
    }
    this.dom.elSettlementMvpCallout.innerHTML =
      calloutParts.join(' <span class="callout-sep">·</span> ') || "🏆 战报统计已完成";

    // 3. 经过 1.15s 棋盘定格高光后，平滑展开分栏毛玻璃战报中心
    this.dom.elModalGameover.classList.remove("inspecting-board");
    this.gameoverPopupTimer = window.setTimeout(() => {
      this.gameoverPopupTimer = null;
      this.dom.elModalGameover.style.display = "flex";
    }, 1150);
  }
}
